import React from "react"
import { Box, CircularProgress, Typography } from "@material-ui/core"
import { useIsSmallScreen } from "../../../hooks/use-is-small-screen"
import {
  useGetHourReportRoleAssignmentOptionsQuery,
  useGetHourReporterMembershipsQuery,
  useUpdateHourReportApproversMutation,
  useUpdateHourReportCheckersMutation,
} from "../../../memberships/api"
import type { HourReporterMembershipViewModel, MemberSummary } from "../../../memberships/api/hour-reporter-membership"
import type { HourReportRoleAssignmentOption } from "../../../memberships/api/schemas"
import { HourReportingRolesCards } from "./components/HourReportingRolesCards"
import { HourReportingRolesTable } from "./components/HourReportingRolesTable"
import { RoleDetailsDialog } from "./components/RoleDetailsDialog"
import { useTranslations } from "./hooks/use-translations"
import { HourReportingRoleRow, RoleAssignmentKind, RoleSelectorOption } from "./types"
import { getMemberById, getReporterDetails } from "./utils"
import { useSnackbar } from "notistack"
import { errorToMessage } from "../../../utils/errors"

type HourReportingRolesProps = {
  orgId: string
  canUpdateCheckersAndApprovers: boolean
  supportsChecking: boolean
}

const HourReportingRoles = ({ orgId, canUpdateCheckersAndApprovers, supportsChecking }: HourReportingRolesProps) => {
  const isSmallScreen = useIsSmallScreen()
  const translations = useTranslations()
  const {
    data: memberships = [],
    isLoading: isLoadingHourReporterMembers,
    isError: isHourReporterMembersError,
  } = useGetHourReporterMembershipsQuery(orgId)
  const {
    data: roleAssignmentOptions = [],
    isLoading: isLoadingRoleAssignmentOptions,
    isError: isRoleAssignmentOptionsError,
  } = useGetHourReportRoleAssignmentOptionsQuery(orgId, {
    skip: !canUpdateCheckersAndApprovers,
  })
  const [updateHourReportCheckers, updateHourReportCheckersMeta] = useUpdateHourReportCheckersMutation()
  const [updateHourReportApprovers, updateHourReportApproversMeta] = useUpdateHourReportApproversMutation()
  const [selectedMemberIdForDetails, setSelectedMemberIdForDetails] = React.useState<string | null>(null)
  const { enqueueSnackbar } = useSnackbar()

  const isAssignmentPending = React.useCallback(
    (memberId: string, kind: RoleAssignmentKind) => {
      if (kind === "checker") {
        return updateHourReportCheckersMeta.isLoading && updateHourReportCheckersMeta.originalArgs?.id === memberId
      }

      return updateHourReportApproversMeta.isLoading && updateHourReportApproversMeta.originalArgs?.id === memberId
    },
    [updateHourReportApproversMeta, updateHourReportCheckersMeta]
  )

  const updateAssignment = async (
    memberId: string,
    kind: RoleAssignmentKind,
    selectedMembers: RoleSelectorOption[]
  ) => {
    if (!canUpdateCheckersAndApprovers || isAssignmentPending(memberId, kind)) return

    const membership = memberships.find((item) => item.id === memberId)
    if (!membership) return

    const previousSelectedIds =
      kind === "checker"
        ? membership.checkers.map((checker) => checker.id)
        : membership.approvers.map((approver) => approver.id)
    const selectedIds = selectedMembers.map((member) => member.id)
    const membershipIdsToAdd = selectedIds.filter((id) => !previousSelectedIds.includes(id))
    const membershipIdsToRemove = previousSelectedIds.filter((id) => !selectedIds.includes(id))

    if (!membershipIdsToAdd.length && !membershipIdsToRemove.length) return

    try {
      if (kind === "approver") {
        await updateHourReportApprovers({
          id: memberId,
          orgId,
          approverMembershipIdsToAdd: membershipIdsToAdd,
          approverMembershipIdsToRemove: membershipIdsToRemove,
        }).unwrap()
        return
      }

      await updateHourReportCheckers({
        id: memberId,
        orgId,
        checkerMembershipIdsToAdd: membershipIdsToAdd,
        checkerMembershipIdsToRemove: membershipIdsToRemove,
      }).unwrap()
    } catch (error) {
      const message = errorToMessage(error, translations)
      enqueueSnackbar(message, { variant: "error" })
    }
  }

  if (
    (isLoadingHourReporterMembers && !memberships.length) ||
    (isLoadingRoleAssignmentOptions && !roleAssignmentOptions.length)
  ) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    )
  }

  if (isHourReporterMembersError || isRoleAssignmentOptionsError) {
    return (
      <Typography variant="body2" color="textSecondary">
        {translations.errorLoadingHourReportingRolesText}
      </Typography>
    )
  }

  if (!memberships.length) {
    return (
      <Typography variant="body2" color="textSecondary">
        {translations.noHourReportingRolesText}
      </Typography>
    )
  }

  const rows = createRows(memberships, roleAssignmentOptions)
  const selectedMemberForDetails = selectedMemberIdForDetails
    ? getMemberById(selectedMemberIdForDetails, memberships)
    : null
  const selectedReporterDetails = selectedMemberIdForDetails
    ? getReporterDetails(selectedMemberIdForDetails, memberships)
    : { checkedReporters: [], approvedReporters: [] }

  const roleDetailsDialog = (
    <RoleDetailsDialog
      open={Boolean(selectedMemberForDetails)}
      member={selectedMemberForDetails}
      checkedReporters={selectedReporterDetails.checkedReporters}
      approvedReporters={selectedReporterDetails.approvedReporters}
      translations={translations}
      supportsChecking={supportsChecking}
      onClose={() => setSelectedMemberIdForDetails(null)}
    />
  )

  if (isSmallScreen) {
    return (
      <>
        <HourReportingRolesCards
          orgId={orgId}
          rows={rows}
          translations={translations}
          canUpdateAssignments={canUpdateCheckersAndApprovers}
          supportsChecking={supportsChecking}
          isAssignmentPending={isAssignmentPending}
          onOpenRoleDetails={setSelectedMemberIdForDetails}
          onUpdateAssignment={updateAssignment}
        />
        {roleDetailsDialog}
      </>
    )
  }

  return (
    <>
      <HourReportingRolesTable
        orgId={orgId}
        rows={rows}
        translations={translations}
        canUpdateAssignments={canUpdateCheckersAndApprovers}
        supportsChecking={supportsChecking}
        isAssignmentPending={isAssignmentPending}
        onOpenRoleDetails={setSelectedMemberIdForDetails}
        onUpdateAssignment={updateAssignment}
      />
      {roleDetailsDialog}
    </>
  )
}

const createRows = (
  memberships: HourReporterMembershipViewModel[],
  roleAssignmentOptions: HourReportRoleAssignmentOption[]
): HourReportingRoleRow[] => {
  return memberships.map((membership) => {
    return {
      member: membership,
      reporterDetails: getReporterDetails(membership.id, memberships),
      roleOptions: getRoleOptions(membership, roleAssignmentOptions),
      selectedCheckers: membership.checkers.map(toRoleSelectorOption),
      selectedApprovers: membership.approvers.map(toRoleSelectorOption),
    }
  })
}

const getRoleOptions = (
  member: MemberSummary,
  roleAssignmentOptions: HourReportRoleAssignmentOption[]
): RoleSelectorOption[] => {
  return roleAssignmentOptions.filter((option) => option.id !== member.id).map(toRoleAssignmentOption)
}

const toRoleSelectorOption = (member: MemberSummary): RoleSelectorOption => {
  return {
    id: member.id,
    name: member.fullname,
  }
}

const toRoleAssignmentOption = (option: HourReportRoleAssignmentOption): RoleSelectorOption => {
  return {
    id: option.id,
    name: option.name,
  }
}

export default HourReportingRoles
